<template>
    <div>
        <v-card>
            <v-card-title>
                {{ webhookType }} Webhooks
                <v-btn
                    class="ml-auto"
                    icon
                    @click="tips = !tips">
                    <v-icon dark>
                        mdi-information
                    </v-icon>
                </v-btn>
            </v-card-title>
            <v-card-text class="p-0">
                <v-alert
                    :value="tips"
                    outlined
                    color="primary"
                    dark
                    border="right"
                    transition="scale-transition"
                    class="mx-5">
                    <div class="title">
                        Tips
                    </div>
                    <p v-for="(tip, index) in webhookTips"
                        :key="'tips-' + index">
                        {{ tip }}
                    </p>
                </v-alert>

                <div v-if="integrationId" class="mx-5">
                    <v-progress-linear
                        v-if="isLoading"
                        indeterminate
                        color="primary"
                        class="mb-5">
                    </v-progress-linear>
                    <v-form
                        ref="form">
                        <v-list>
                            <div v-if="type == 'Shopify' || type == 'Shopify Update Only'">
                                <v-col cols="12" md="12" sm="12" lg="5"  class="p-0">
                                    <div class="d-flex justify-end">
                                        <v-tooltip bottom>
                                            <template slot="activator" slot-scope="{ on }">
                                                <v-btn small :disabled="isLoading" v-on="on" @click="triggerTopic('default', true)">
                                                    <v-icon dark class="mr-3 info-icon-stack">
                                                        mdi-information
                                                    </v-icon>
                                                    Enable Default Webhooks
                                                </v-btn>
                                            </template>
                                            <span>
                                                <strong>Enable the following:</strong><br>
                                                ORDERS_UPDATED<br>
                                                ORDERS_CANCELLED<br>
                                                ORDERS_DELETE<br>
                                                FULFILLMENT_ORDERS_SPLIT<br>
                                                FULFILLMENT_ORDERS_ORDER_ROUTING_COMPLETE<br>
                                                FULFILLMENT_ORDERS_PLACED_ON_HOLD<br>
                                                FULFILLMENT_ORDERS_HOLD_RELEASED
                                            </span>
                                        </v-tooltip>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="12" sm="12" lg="5" class="p-0">
                                    <v-list-item class="hoverable-list-item">
                                        <v-list-item-content>
                                            <strong>Available Webhooks</strong>
                                        </v-list-item-content>
                                        <v-list-item-action>
                                            <div>
                                                <strong>Enabled</strong>
                                            </div>
                                        </v-list-item-action>
                                    </v-list-item>
                                </v-col>
                                <v-col cols="12" md="12" sm="12" lg="5" class="p-0">
                                    <v-list-item v-for="shopifyHook in shopifyWebhooks" :key="'webhook-' + shopifyHook.text" class="hoverable-list-item">
                                        <v-list-item-content>
                                            {{ shopifyHook.text.toUpperCase() }}
                                        </v-list-item-content>
                                        <v-list-item-action>
                                            <div>
                                                <v-switch :input-value="hasWebhook(shopifyHook.text)" @change="triggerTopic(shopifyHook.text, $event)" :disabled="isLoading" color="primary" class="mb-0"></v-switch>
                                            </div>
                                        </v-list-item-action>
                                    </v-list-item>
                                </v-col>
                            </div>
                        </v-list>
                    </v-form>

                </div>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { EventBus } from '@/js/event-bus.js'

export default {
    props: {
        id: { type: Number, default: null },
        type: { type: String, default: "" },
    },

    computed: {
        webhookType() {
            return this.type
        },

        integrationId() {
            return this.id
        },

        hasOrdersCreate() {
            return this.webhooks.some(function(field) {
                return field.topic === 'ORDERS_CREATE'
            });
        },

        hasOrdersUpdated() {
            return this.webhooks.some(function(field) {
                return field.topic === 'ORDERS_UPDATED'
            });
        }
    },


    data() {
        return {
            isLoading: false,
            webhooks: [],
            tips: false,
            webhookTips: [],
            shopifyWebhooks: [
                { text: 'ORDERS_CREATE', value: 'Received every creation of Shopify order.'},
                { text: 'ORDERS_UPDATED', value: 'Received every update of Shopify order.'},
                { text: 'ORDERS_CANCELLED', value: 'Occurs whenever an order is cancelled.'},
                { text: 'ORDERS_DELETE', value: 'Occurs whenever an order is deleted.'},
                { text: 'FULFILLMENT_ORDERS_MOVED', value: 'Shopify order whose location has been changed.'},
                { text: 'FULFILLMENT_ORDERS_SPLIT', value: 'Fulfillment order has had items split into a new FO.'},
                { text: 'FULFILLMENT_ORDERS_ORDER_ROUTING_COMPLETE', value: 'Occurs when an order has finished being routed and assigned to a fulfillment location.'},
                { text: 'FULFILLMENT_ORDERS_PLACED_ON_HOLD', value: 'Occurs when a fulfillment order is placed on hold.'},
                { text: 'FULFILLMENT_ORDERS_HOLD_RELEASED', value: 'Occurs when a fulfillment order hold is released.'},
                { text: 'FULFILLMENTS_UPDATE', value: 'Occurs whenever a fulfillment is updated.'},
            ],
        }
    },

    mounted() {
        this.fetchWebhooks();
    },

    methods: {

        hasWebhook(text) {
            return this.webhooks.some(function(field) {
                return field.topic === text
            });
        },

        fetchWebhooks() {
            // Guard: ensure required props exist
            if (!this.id || !this.type) {
                return;
            }

            const t = (this.type || '').toLowerCase();

            // Build tips for Shopify GraphQL webhooks
            if (t === 'shopify') {
                this.webhookTips = this.shopifyWebhooks.map(hook => {
                    const desc = hook.value;
                    return desc ? `${hook.text} - ${desc}` : hook.text;
                });
            }

            // Use GraphQL endpoint
            this.$http
                .get('/api/webhooks/shopify-graphql/' + this.id)
                .then(({ data }) => {
                    if (data.code == 200) {
                        this.webhooks = (data.result.webhooks || []).map(webhook => {
                            return { topic: webhook.topic, id: webhook.id };
                        });
                    }
                });
        },

        async triggerTopic(topic, isEnabled) {

            let getId = null;
            this.webhooks.map(webhook => {
                if (webhook.topic == topic) {
                    getId = webhook.id
                }
            })

            // stops spam
            if (this.isLoading) {
                return;
            }

            try {
                this.isLoading = true

                if (isEnabled) {
                    // User wants to enable webhook - create it via GraphQL
                    const subsData = {
                        'topic':  topic
                    };
                    this.$http.post('/api/webhooks/shopify-graphql/subscribe/' + this.id, subsData)
                    .then((data) => {
                        if (data.data.code == 200 || data.data.code == 201) {
                            this.fetchWebhooks()
                            EventBus.$emit('show-success', 'Successfully subscribed to ' + topic + ' (GraphQL).');
                        }
                        else if (data.data.errors) {
                            // Show detailed error message
                            let errorMsg = Array.isArray(data.data.errors) ? data.data.errors.join(', ') : JSON.stringify(data.data.errors);
                            EventBus.$emit('show-error', 'Failed to subscribe: ' + errorMsg);
                        } else {
                            EventBus.$emit('show-error', 'Failed to subscribe to ' + topic + '. Unknown error.');
                        }
                        this.isLoading = false
                    })
                    .catch(err => {
                        let errorMsg = 'Failed to subscribe to webhook';
                        if (err.response?.data?.errors) {
                            errorMsg = Array.isArray(err.response.data.errors) ? err.response.data.errors.join(', ') : JSON.stringify(err.response.data.errors);
                        } else if (err.response?.data?.message) {
                            errorMsg = err.response.data.message;
                        } else if (err.message) {
                            errorMsg = err.message;
                        }
                        EventBus.$emit('show-error', errorMsg);
                        this.isLoading = false
                    })
                }
                else {
                    // User wants to disable webhook - delete it via GraphQL
                    if (getId) {
                        this.$http.get('/api/webhooks/shopify-graphql/delete/' + this.id + '/' + getId)
                        .then((data) => {
                            if (data.data.code == 200) {
                                this.fetchWebhooks()
                                EventBus.$emit('show-success', 'Successfully unsubscribed from ' + topic + ' (GraphQL).');
                            }
                            else if (data.data.errors) {
                                let errorMsg = Array.isArray(data.data.errors) ? data.data.errors.join(', ') : JSON.stringify(data.data.errors);
                                EventBus.$emit('show-error', 'Failed to unsubscribe: ' + errorMsg);
                            } else {
                                EventBus.$emit('show-error', 'Failed to unsubscribe from ' + topic + '. Unknown error.');
                            }
                            this.isLoading = false
                        })
                        .catch(err => {
                            let errorMsg = 'Failed to unsubscribe from webhook';
                            if (err.response?.data?.errors) {
                                errorMsg = Array.isArray(err.response.data.errors) ? err.response.data.errors.join(', ') : JSON.stringify(err.response.data.errors);
                            } else if (err.response?.data?.message) {
                                errorMsg = err.response.data.message;
                            } else if (err.message) {
                                errorMsg = err.message;
                            }
                            EventBus.$emit('show-error', errorMsg);
                            this.isLoading = false
                        })
                    } else {
                        // No webhook ID found, just refresh the state
                        this.fetchWebhooks()
                        this.isLoading = false
                        EventBus.$emit('show-warning', 'No webhook found to delete for ' + topic);
                    }
                }

            } catch (err) {
                EventBus.$emit('show-error', err.message || 'An error occurred');
                this.isLoading = false
            }
        }
    }
}
</script>

<style scoped>
.hoverable-list-item {
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
  border-radius: 6px;
}

/* Fallback hover for all themes (works even if theme classes are outside scope) */
.hoverable-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  /* underline without affecting layout */
  box-shadow: inset 0 -1px #e0e0e0;
}

/* Light theme hover */
.theme--light .hoverable-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  /* underline without affecting layout */
  box-shadow: inset 0 -1px #e0e0e0;
}

/* Dark theme hover */
.theme--dark .hoverable-list-item:hover {
  background-color: rgba(255, 255, 255, 0.06);
  box-shadow: inset 0 -1px rgba(255, 255, 255, 0.2);
}

/* Optional: show the underline when focused via keyboard within the item */
.hoverable-list-item:focus-within {
  box-shadow: inset 0 -1px currentColor;
}
</style>
